import type { ModelViewerSectionVisibilityCallback } from "@centricsoftware/model-viewer";
import locale from "csi/locale";
import { ALVA_MESH, AVATAR } from "csi/threeD/utility/resources";
import { cnl } from "csi/typings";
import { arrayLast } from "csi/utility/arrayUtility";
import { ReactionStore } from "csi/utility/mobxUtility";
import { CheckboxTreeNode, CheckboxTreeStore } from "designSystem/components/checkbox/CheckboxTree";
import { ComponentItem } from "designSystem/types/component-item-types";
import { action, computed, makeObservable, observable } from "mobx";

export interface C8ThreeDMesh {
    castShadow: boolean;
    children?: object[];
    frustumCulled: boolean;
    layers: object;
    materialLibraries?: string[];
    name?: string;
    position: object;
    rotation: object;
    scale?: object;
    type: string;
    uuid: string;
    visible: boolean;
}

export interface AvatarData {
    visible: boolean;
    uuid: string;
}

export default class CanvasStore extends ReactionStore {
    /** Stores the selected environment lighting url */
    @observable selectedEnvironmentUrl: cnl;

    /** Stores the selected environment lighting background */
    @observable environmentBackground: boolean;

    /** Stores the selected environment lighting tone mapping exposure */
    @observable environmentExposure: number;

    /** Stores the selected environment lighting directional light position */
    @observable environmentOrientation: number;

    /** Model Variants */
    @observable.ref modelVariants: string[];
    
    /** 3D Mesh data */
    @observable.ref meshData: THREE.Object3D;

    /** Variant Name */
    @observable variantName: string | undefined = undefined;

    /** AlvaMesh Avatar Data to toggle */
    @observable.ref avatarData: AvatarData | undefined = undefined;

    /** Update Mesh visibility */
    @observable.ref changeModelSectionVisibility: ModelViewerSectionVisibilityCallback;

    /** Entry point for ModelViewer update material variants */
	@observable.ref onChangeModelVariant: (variant: string) => void;

	/** The Checkbox store to renderer the checkbox nodes for model components */
	@observable.ref modelComponentsCheckboxStore: CheckboxTreeStore;
    
    /** Set the Model Variants */
    @action setModelVariants = (variants: string[]): void => {
    	this.modelVariants = variants;
    };

    /** Computed value for variants */
    @computed get variants(): ComponentItem[] {
    	return this.modelVariants?.map((variant) => {
    		return {
    			id: variant,
    		};
    	});
    }
    
    /** Callback for the checkbox tree to set component visibility */ 
    @action onCheckboxChangeCallback = (node: CheckboxTreeNode): void => {
    	if (!node.hasChildren) {
    		this.changeModelSectionVisibility(node.uniqueId, node.state);
    	} 
    	if (node.uniqueId === this.avatarData?.uuid){
    		this.setAvatarVisibility(node.displayChecked);
    	}
    };
    
    /** Set the Nodes of the checkbox tree */
    @action createModelComponentCheckboxNodes = (mesh: C8ThreeDMesh, parent?: CheckboxTreeNode): void => {
    	mesh.children?.forEach((child: THREE.Object3D | THREE.Group | THREE.Mesh) => {
    		const childName = child.name || locale.resource("NAME_EMPTY");
    		const defaultState = child.visible;
    		const node = this.modelComponentsCheckboxStore.createNode(child.uuid, childName, defaultState, parent);
    		this.createModelComponentCheckboxNodes(child, node);
    	});
    };
    
    /** set the avatar visibility */
    @action setAvatarVisibility = (state: boolean): void => {
    	if (this.avatarData) {
    		const { uuid } = this.avatarData;
    		this.avatarData = {
    			visible: state,
    			uuid,
    		};
    		this.changeModelSectionVisibility(uuid, state);
    	}
    };

    /** toggle avatar visible from left menu */
    @action toggleAvatarVisibility = (): void => {
    	if (!this.avatarData) {
    		return;
    	}
    	const newState = !this.avatarData.visible;
    	this.setAvatarVisibility(newState);
    	this.travserseAndUpdateCheckboxNode(newState);
    };

    /** Traverse and update children */
    private travserseAndUpdateCheckboxNode(state: boolean): void {
    	const traverseAndUpdate = (node: CheckboxTreeNode): void => {
    		if (node.uniqueId === this.avatarData?.uuid) {
    			node.state = state;
    			this.updateChildrenState(node, state);
    		} else if (node.children) {
    			node.children.forEach((child) => {
    				traverseAndUpdate(child);
    			});
    		}
    	};

    	this.modelComponentsCheckboxStore.children.forEach((node) => {
    		traverseAndUpdate(node);
    	});
    }

    /** Update all children nodes to match the given state */
    private updateChildrenState(node: CheckboxTreeNode, state: boolean): void {
    	node.children?.forEach((child) => {
    		child.state = state;
    		if (!child.hasChildren){
    			this.changeModelSectionVisibility(child.uniqueId, state);
    		}
    		this.updateChildrenState(child, state);
    	});
    }
        
    /** Determine if the meshData has an AlvaMesh in it */
    private findAvatarData(): void {
    	const { meshData } = this;
    	meshData.traverse((child) => {
    		if (child.name === ALVA_MESH || child.name === AVATAR) {
    			this.avatarData = {
    				visible: child.visible,
    				uuid: child.uuid,
    			};
    		}
    	});
    };

    /** Update variant in ModelViewer */
    @action setModelViewerUpdateVariant = (item: ComponentItem): void => {
    	this.onChangeModelVariant?.(item.id);
    	this.setVariantName(item.id);
    };

    /** Set the variant name */
    @action setVariantName = (variantName?: string): void => {
    	this.variantName = variantName;
    };

    /** Set the Mesh Data provided from ModelViewer */
    @action setMeshData = (meshData: THREE.Object3D): void => {
    	this.meshData = meshData;
    	this.findAvatarData();
    	this.createModelComponentCheckboxNodes(this.meshData);
    };

    /** Get the default variant name to be displayed */
    @computed get defaultVariantName(): string | undefined {
    	return this.variantName || arrayLast(this.modelVariants);
    }

    constructor(variantName?: string) {
    	super();
    	makeObservable(this);
    	this.setVariantName(variantName);
    	this.modelComponentsCheckboxStore = new CheckboxTreeStore(null, null, this.onCheckboxChangeCallback);
    }
}